import {
  type CSSProperties,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import { RiCloseLine } from '@remixicon/react';

import { Box, Button, ButtonIcon, Text } from '@spotify-internal/encore-web';

import type { DraftFeedback } from './reviewFeedback';

import styles from './FeedbackTray.module.css';

type HorizontalBounds = {
  left: number;
  width: number;
};

type FeedbackDeckStyle = CSSProperties & {
  '--feedback-count': number;
};

type FeedbackTrayProps = {
  anchorRef: RefObject<HTMLDivElement | null>;
  feedbackCount: number;
  feedbackItems: DraftFeedback[];
  isSubmitting: boolean;
  onFinish: () => void;
  onRemove: (id: string) => void;
};

export const FeedbackTray = ({
  anchorRef,
  feedbackCount,
  feedbackItems,
  isSubmitting,
  onFinish,
  onRemove,
}: FeedbackTrayProps) => {
  const [bounds, setBounds] = useState<HorizontalBounds | null>(null);
  const positionFrameRef = useRef<number | null>(null);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return undefined;
    }

    const updateBounds = (): void => {
      const nextBounds = anchor.getBoundingClientRect();
      setBounds(currentBounds => {
        if (
          currentBounds?.left === nextBounds.left &&
          currentBounds.width === nextBounds.width
        ) {
          return currentBounds;
        }

        return { left: nextBounds.left, width: nextBounds.width };
      });
    };
    const scheduleBoundsUpdate = (): void => {
      if (positionFrameRef.current !== null) {
        return;
      }

      positionFrameRef.current = window.requestAnimationFrame(() => {
        positionFrameRef.current = null;
        updateBounds();
      });
    };
    const resizeObserver = new ResizeObserver(scheduleBoundsUpdate);

    updateBounds();
    resizeObserver.observe(anchor);
    window.addEventListener('resize', scheduleBoundsUpdate);
    window.addEventListener('scroll', scheduleBoundsUpdate, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleBoundsUpdate);
      window.removeEventListener('scroll', scheduleBoundsUpdate, true);
      if (positionFrameRef.current !== null) {
        window.cancelAnimationFrame(positionFrameRef.current);
        positionFrameRef.current = null;
      }
    };
  }, [anchorRef]);

  const positionStyle: CSSProperties = bounds
    ? { left: bounds.left, width: bounds.width }
    : { visibility: 'hidden' };
  const deckStyle: FeedbackDeckStyle = {
    '--feedback-count': feedbackItems.length,
  };
  const finishButtonLabel = feedbackCount
    ? `Submit feedback (${feedbackCount})`
    : 'Finish review';

  return (
    <div className={styles.feedbackTrayPositioner} style={positionStyle}>
      <div className={styles.feedbackTrayStack}>
        {feedbackItems.length > 0 && (
          <Box
            as="section"
            variant="naked"
            className={styles.feedbackDrafts}
            aria-label="Pending feedback"
            style={deckStyle}
          >
            <div className={styles.feedbackList}>
              {feedbackItems.map((item, index) => (
                <Box
                  key={item.id}
                  variant="elevated"
                  className={styles.feedbackItem}
                >
                  <div className={styles.feedbackItemText}>
                    <Text
                      as="blockquote"
                      variant="marginal"
                      semanticColor="textSubdued"
                      lineClamp={1}
                    >
                      {item.quote}
                    </Text>
                    <Text as="p" variant="marginal" lineClamp={2}>
                      {item.feedback}
                    </Text>
                  </div>
                  <ButtonIcon
                    type="button"
                    variant="tertiary"
                    size="small"
                    aria-label={`Remove feedback ${index + 1}`}
                    disabled={isSubmitting}
                    onClick={() => onRemove(item.id)}
                   data-track-source-anchor="74d34dcf8b;feedback-tray-button-icon;low">
                    <RiCloseLine size={16} aria-hidden />
                  </ButtonIcon>
                </Box>
              ))}
            </div>
          </Box>
        )}
        <Box
          as="section"
          variant="elevated"
          className={styles.feedbackActions}
          aria-label="Draft feedback"
        >
          <Text as="p" variant="bodySmall" semanticColor="textSubdued">
            {feedbackCount === 0
              ? 'Select text, write your feedback'
              : `${feedbackCount} ${feedbackCount === 1 ? 'feedback item' : 'feedback items'}`}
          </Text>
          <Button
            type="button"
            variant="primary"
            size="small"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            loadingAriaLabel="Finishing review"
            onClick={onFinish}
           data-track-source-anchor="250c1e5cf1;feedback-tray-button">
            {finishButtonLabel}
          </Button>
        </Box>
      </div>
    </div>
  );
};
